import React, { useState, useEffect, useRef, useMemo } from "react";
import { motion, AnimatePresence, useMotionValue, useTransform } from "framer-motion";
import { loadDhikrChapters } from "../../../api/dhikr";
import "./adhkar.css";

const DhikrCard = ({
  item,
  index,
  direction,
  variants,
  canGoNext,
  canGoPrev,
  goToNextDhikr,
  goToPrevDhikr,
  handleDhikrCardClick,
  showRepeat,
  repeatTarget,
  displayedRepeats,
  currentAudioSrc,
  isPlaying,
  handlePlayDhikr,
  showTransliteration,
  showTranslation,
  showFootnotes
}) => {
  const x = useMotionValue(0);
  const rotateDrag = useTransform(x, [-300, 300], [-15, 15]);
  const opacityDrag = useTransform(x, [-500, 0, 500], [0, 1, 0]);

  return (
    <motion.div
      key={index}
      custom={direction}
      variants={variants}
      initial="enter"
      animate="center"
      exit="exit"
      style={{ x, rotate: rotateDrag, opacity: opacityDrag, touchAction: "pan-y" }}
      transition={{
        x: { type: "spring", stiffness: 160, damping: 28, bounce: 0 },
        opacity: { duration: 0.4 },
        rotate: { duration: 0.4 },
        scale: { duration: 0.4 }
      }}
      drag="x"
      dragDirectionLock
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.6}
      onDragEnd={(e, info) => {
        const { offset, velocity } = info;
        const swipeThreshold = 60;
        const velocityThreshold = 600;
        
        // Very strict check: movement must be primarily horizontal
        // to trigger navigation. Otherwise, assume it was a scroll.
        const isHorizontalSwipe = Math.abs(offset.x) > Math.abs(offset.y) * 2;

        if (isHorizontalSwipe) {
          if (offset.x < -swipeThreshold && canGoNext) {
            goToNextDhikr();
          } else if (offset.x > swipeThreshold && canGoPrev) {
            goToPrevDhikr();
          } else if (Math.abs(velocity.x) > velocityThreshold) {
            if (velocity.x < 0 && canGoNext) {
              goToNextDhikr();
            } else if (velocity.x > 0 && canGoPrev) {
              goToPrevDhikr();
            }
          }
        }
      }}
      className="dhikr-swipe-card"
    >
      <div
        className="adhkar-card"
        onClick={handleDhikrCardClick}
        role={showRepeat ? "button" : undefined}
        tabIndex={showRepeat ? 0 : undefined}
      >
        <div className="adhkar-card-top">
          {showRepeat && (
            <div className="repeat-badge">
              Répétitions : {displayedRepeats === 0 ? repeatTarget : `${displayedRepeats}/${repeatTarget}`}
            </div>
          )}
          {item.audio_url && (
            <button
              className={`adhkar-play-btn ${currentAudioSrc === item.audio_url && isPlaying ? 'active' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                handlePlayDhikr(item.audio_url);
              }}
              title="Écouter ce dhikr"
              type="button"
            >
              {currentAudioSrc === item.audio_url && isPlaying ? '⏸' : '▶'}
            </button>
          )}
        </div>

        <p className="adhkar-arabic">{item.arabic}</p>
        {showTransliteration && (
          <p className="adhkar-transliteration">
            <em>{item.transliteration}</em>
          </p>
        )}
        {showTranslation && <p className="adhkar-translation">{item.translation.fr}</p>}
        {item.notes && item.notes.fr && showFootnotes && (
          <hr className="dhikr-note-divider" />
        )}
        {item.notes && item.notes.fr && showFootnotes && (
          <p className="adhkar-note">
            <strong>Note:</strong> {item.notes.fr}
          </p>
        )}
        
        {/* Bottom spacer to ensure text clears navigation buttons when scrolling */}
        <div className="adhkar-card-bottom-spacer" />
      </div>
    </motion.div>
  );
};

// Icons for categories
const CATEGORY_ICONS = {
  cat_01: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"></path><path d="M19 3v4"></path><path d="M21 5h-4"></path></svg>,
  cat_02: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.38 3.46 16 2a4 4 0 0 1-8 0L3.62 3.46a2 2 0 0 0-1.34 1.88v2.24c0 .7.39 1.32 1 1.63L5 9.16l1.34.45v10.59a2 2 0 0 0 2 2h7.32a2 2 0 0 0 2-2V9.61l1.34-.45 1.7-1.05c.61-.31 1-.93 1-1.63V5.34a2 2 0 0 0-1.34-1.88Z"></path><path d="m12 11 1.5 2h-3L12 11Z"></path></svg>,
  cat_03: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>,
  cat_04: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 21h20"></path><path d="M4 21V11s1-2 4-2c2 0 4 1 4 3"></path><path d="M10 21V10c0-2.5 2-4.5 4.5-4.5S19 7.5 19 10v11"></path><path d="M14 21v-4a2 2 0 0 1 4 0v4"></path></svg>,
  cat_05: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4"></circle><path d="M12 2v2"></path><path d="M12 20v2"></path><path d="M4.93 4.93l1.41 1.41"></path><path d="M17.66 17.66l1.41 1.41"></path><path d="M2 12h2"></path><path d="M20 12h2"></path><path d="m6.34 17.66-1.41 1.41"></path><path d="m19.07 4.93-1.41 1.41"></path><path d="M15.5 12.5a4.5 4.5 0 01-2.5 1.5 9 9 0 006 3 9 9 0 00-3.5-4.5z"></path></svg>,
  cat_06: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"></path><path d="M7 2v20"></path><path d="M21 15V2v0a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7"></path></svg>,
  cat_07: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2 11 13"></path><path d="m22 2-7 20-4-9-9-4 20-7Z"></path></svg>,
  cat_08: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>,
  cat_09: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"></path></svg>,
  cat_10: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z"></path><path d="M2 21c0-3 1.85-5.36 5.08-6"></path></svg>,
  cat_11: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M22 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>,
  cat_12: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l10 5-10 5L2 7l10-5z"></path><path d="M2 7v10l10 5V12L2 7z"></path><path d="M22 7v10l-10 5V12l10-5z"></path><path d="M7 14.5l5 2.5 5-2.5"></path></svg>,
  cat_13: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 11V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v0"></path><path d="M14 10V4a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v2"></path><path d="M10 10.5V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v8"></path><path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15"></path></svg>
};

const normalizeCategoryId = (value) => {
  if (value === null || value === undefined) return "";
  const raw = String(value).trim().toLowerCase();
  if (!raw) return "";

  // Accept ids like: cat_1, cat-1, cat 1, 1
  const m = raw.match(/^cat[_\-\s]?(\d+)$/) || raw.match(/^(\d+)$/);
  if (m) {
    const n = Number(m[1]);
    if (Number.isFinite(n)) return `cat_${String(n).padStart(2, "0")}`;
  }

  return raw;
};

const normalizeImageSrc = (src) => {
  if (!src) return "";
  const s = String(src).trim();
  if (!s) return "";
  if (s.startsWith("http://") || s.startsWith("https://") || s.startsWith("/")) return s;
  return `/${s}`;
};

const getCategoryImageSrc = (cat) => {
  return (
    normalizeImageSrc(cat?.image) ||
    normalizeImageSrc(cat?.imageUrl) ||
    normalizeImageSrc(cat?.img) ||
    ""
  );
};

const getCategoryVisual = (cat) => {
  const imageSrc = getCategoryImageSrc(cat);
  if (imageSrc) {
    return <img src={imageSrc} alt="" loading="lazy" decoding="async" aria-hidden="true" />;
  }

  const key = normalizeCategoryId(cat?.id);
  return CATEGORY_ICONS[key] || CATEGORY_ICONS.cat_03;
};

const AdhkarPage = () => {
  const [chapters, setChapters] = useState([]);
  const [filteredChapters, setFilteredChapters] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [currentDhikrIndex, setCurrentDhikrIndex] = useState(0);
  const [currentRepeatCount, setCurrentRepeatCount] = useState(0);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [recentDhikr, setRecentDhikr] = useState(() => {
    try {
      const saved = localStorage.getItem("adhkar_recent_dhikr");
      const parsed = saved ? JSON.parse(saved) : [];
      if (!Array.isArray(parsed)) return [];

      // Normalize older data: keep at most 4 unique chapters (latest first)
      const seenChapterIds = new Set();
      const normalized = [];
      for (const entry of parsed) {
        const chapterId = entry?.chapterId;
        if (!chapterId || seenChapterIds.has(chapterId)) continue;
        seenChapterIds.add(chapterId);
        normalized.push(entry);
        if (normalized.length >= 4) break;
      }
      return normalized;
    } catch {
      return [];
    }
  });
  const [loading, setLoading] = useState(true);
  const [showTransliteration, setShowTransliteration] = useState(() => {
    const saved = localStorage.getItem("adhkar_show_transliteration");
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [showTranslation, setShowTranslation] = useState(() => {
    const saved = localStorage.getItem("adhkar_show_translation");
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [showFootnotes, setShowFootnotes] = useState(() => {
    const saved = localStorage.getItem("adhkar_show_footnotes");
    return saved !== null ? JSON.parse(saved) : false;
  });
  const [direction, setDirection] = useState(0);

  useEffect(() => {
    localStorage.setItem("adhkar_show_transliteration", JSON.stringify(showTransliteration));
  }, [showTransliteration]);

  useEffect(() => {
    localStorage.setItem("adhkar_show_translation", JSON.stringify(showTranslation));
  }, [showTranslation]);

  useEffect(() => {
    localStorage.setItem("adhkar_show_footnotes", JSON.stringify(showFootnotes));
  }, [showFootnotes]);

  useEffect(() => {
    try {
      localStorage.setItem("adhkar_recent_dhikr", JSON.stringify(recentDhikr));
    } catch {
      // ignore
    }
  }, [recentDhikr]);

  // Audio state
  const audioRef = useRef(null);
  const searchBlurTimeoutRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentAudioSrc, setCurrentAudioSrc] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchChapters();
  }, []);

  useEffect(() => {
    const filtered = chapters.filter(chapter => 
      (chapter.title.fr && chapter.title.fr.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (chapter.title.ar && chapter.title.ar.includes(searchTerm))
    );
    setFilteredChapters(filtered);
  }, [searchTerm, chapters]);

  // Handle source change and play
  useEffect(() => {
    if (audioRef.current && currentAudioSrc && isPlaying) {
      audioRef.current.load();
      audioRef.current.play().catch(e => console.error("Playback failed", e));
    }
  }, [currentAudioSrc, isPlaying]);

  // Handle audio ended
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleEnded = () => setIsPlaying(false);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  const fetchChapters = async () => {
    try {
      setLoading(true);
      const data = await loadDhikrChapters();
      setChapters(data);
      setFilteredChapters(data);
    } catch (error) {
      console.error("Failed to load adhkar:", error);
    } finally {
      setLoading(false);
    }
  };

  const categories = useMemo(() => Array.from(
    new Map(
      chapters
        .filter(c => c.category?.id)
        .map(c => [c.category.id, {
          id: c.category.id,
          ar: c.category.ar,
          en: c.category.en,
          fr: c.category.fr,
          image: c.category.image,
          imageUrl: c.category.imageUrl,
          img: c.category.img,
        }])
    ).values()
  ).sort((a, b) => a.id.localeCompare(b.id, undefined, { numeric: true })), [chapters]);

  const handleBackToChapters = () => {
    setSelectedChapter(null);
    setCurrentDhikrIndex(0);
    setCurrentRepeatCount(0);
    window.scrollTo(0, 0);
    setIsPlaying(false);
    setCurrentAudioSrc("");
  };


  const recordRecentDhikr = ({ chapter, dhikrIndex }) => {
    if (!chapter?.id) return;
    const item = chapter.items?.[dhikrIndex];
    const dhikrArabicSnippet = item?.arabic ? String(item.arabic).slice(0, 70) : "";
    const dhikrTranslationSnippet = item?.translation?.fr ? String(item.translation.fr).slice(0, 80) : "";

    const entry = {
      chapterId: chapter.id,
      dhikrIndex,
      chapterTitleFr: chapter.title?.fr || "",
      chapterTitleAr: chapter.title?.ar || "",
      dhikrArabicSnippet,
      dhikrTranslationSnippet,
      ts: Date.now(),
    };

    setRecentDhikr((prev) => {
      // Keep only one entry per chapter, most recent first
      const deduped = prev.filter((e) => e?.chapterId !== entry.chapterId);
      return [entry, ...deduped].slice(0, 4);
    });
  };

  const removeRecentChapter = (chapterId) => {
    if (!chapterId) return;
    setRecentDhikr((prev) => prev.filter((e) => e?.chapterId !== chapterId));
  };

  const openDhikrFromRecent = (entry) => {
    const chapter = chapters.find((c) => c.id === entry?.chapterId);
    if (!chapter) return;

    setSearchTerm("");
    setIsSearchFocused(false);
    setSelectedChapter(chapter);
    setCurrentDhikrIndex(Math.max(0, Math.min(Number(entry?.dhikrIndex) || 0, (chapter.items?.length || 1) - 1)));
    setCurrentRepeatCount(0);
    setIsPlaying(false);
    setCurrentAudioSrc("");
    window.scrollTo(0, 0);
  };

  const handleChapterClick = (chapter) => {
    setSearchTerm("");
    setIsSearchFocused(false);
    setSelectedChapter(chapter);
    setCurrentDhikrIndex(0);
    setCurrentRepeatCount(0);
    setCurrentAudioSrc(chapter.audio_url || "");
    window.scrollTo(0, 0);
    setIsPlaying(false);
    recordRecentDhikr({ chapter, dhikrIndex: 0 });
  };

  const stopAudio = () => {
    if (!audioRef.current) return;
    audioRef.current.pause();
    setIsPlaying(false);
  };

  const canGoPrev = selectedChapter?.items?.length ? currentDhikrIndex > 0 : false;
  const canGoNext = selectedChapter?.items?.length
    ? currentDhikrIndex < selectedChapter.items.length - 1
    : false;

  const goToNextDhikr = () => {
    if (!selectedChapter?.items?.length) return;
    if (currentDhikrIndex >= selectedChapter.items.length - 1) return;
    setDirection(1);
    const nextIndex = currentDhikrIndex + 1;
    stopAudio();
    setCurrentAudioSrc("");
    setCurrentRepeatCount(0);
    setCurrentDhikrIndex(nextIndex);
    recordRecentDhikr({ chapter: selectedChapter, dhikrIndex: nextIndex });
  };

  const goToPrevDhikr = () => {
    if (!selectedChapter?.items?.length) return;
    if (currentDhikrIndex <= 0) return;
    setDirection(-1);
    const prevIndex = currentDhikrIndex - 1;
    stopAudio();
    setCurrentAudioSrc("");
    setCurrentRepeatCount(0);
    setCurrentDhikrIndex(prevIndex);
    recordRecentDhikr({ chapter: selectedChapter, dhikrIndex: prevIndex });
  };

  const handleDhikrCardClick = () => {
    if (!selectedChapter?.items?.length) return;
    const item = selectedChapter.items[currentDhikrIndex];
    const repeatTarget = Number(item?.repeat ?? 0);

    if (!Number.isFinite(repeatTarget) || repeatTarget <= 0) return;

    const nextCount = Math.min(currentRepeatCount + 1, repeatTarget);
    setCurrentRepeatCount(nextCount);

    if (nextCount >= repeatTarget) {
      // Auto-advance when repeat target is reached
      if (currentDhikrIndex < selectedChapter.items.length - 1) {
        goToNextDhikr();
      }
    }
  };

  const variants = {
    enter: (direction) => ({
      x: direction > 0 ? 800 : -800,
      opacity: 0,
      scale: 0.9,
      rotate: direction > 0 ? 25 : -25,
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
      scale: 1,
      rotate: 0,
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 800 : -800,
      opacity: 0,
      scale: 0.9,
      rotate: direction < 0 ? 25 : -25,
    }),
  };

  const handlePlayDhikr = (audioUrl) => {
    if (!audioRef.current) return;
    
    if (currentAudioSrc === audioUrl) {
      togglePlay();
    } else {
      setCurrentAudioSrc(audioUrl);
      setIsPlaying(true);
      // We rely on the useEffect or the audio tag src update to trigger play
    }
  };

  const togglePlay = () => {
    if (audioRef.current.paused) {
      audioRef.current.play();
      setIsPlaying(true);
    } else {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  if (loading) {
    return (
      <div className="blog-area gray-300">
        <div className="adhkar-container container position-relative pt-110">
          <div className="loading-spinner">Chargement des invocations...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-area gray-300">
      <div className={`adhkar-container container position-relative ${selectedChapter ? 'adhkar-reader' : 'pt-110'}`}>
        {selectedChapter ? (
          <div className="chapter-detail">
            <div className="adhkar-reader-topbar">
              <div className="adhkar-reader-chapter-title" aria-label="Titre du chapitre">
                {selectedChapter?.title?.ar && (
                  <div className="adhkar-reader-chapter-title-ar">{selectedChapter.title.ar}</div>
                )}
                {selectedChapter?.title?.fr && (
                  <div className="adhkar-reader-chapter-title-fr">{selectedChapter.title.fr}</div>
                )}
                {selectedChapter?.items?.length ? (
                  <div className="adhkar-reader-position" aria-label="Position du dhikr">
                    {currentDhikrIndex + 1}/{selectedChapter.items.length}
                  </div>
                ) : null}
              </div>

              <div className="adhkar-reader-controls-row">
                <button
                  className="category-back-btn"
                  onClick={handleBackToChapters}
                  aria-label="Retour aux chapitres"
                  title="Retour aux chapitres"
                  type="button"
                >
                  ←
                </button>

                <div className="toggle-group" aria-label="Options d'affichage">
                  <button
                    className={`pill-toggle ${showTransliteration ? 'active' : ''}`}
                    onClick={() => setShowTransliteration(!showTransliteration)}
                    title="Afficher/Masquer la phonétique"
                    type="button"
                  >
                    <span className="icon">abc</span> Phonétique
                  </button>
                  <button
                    className={`pill-toggle ${showTranslation ? 'active' : ''}`}
                    onClick={() => setShowTranslation(!showTranslation)}
                    title="Afficher/Masquer la traduction"
                    type="button"
                  >
                    <span className="icon">文</span> Traduction
                  </button>
                  <button
                    className={`pill-toggle ${showFootnotes ? 'active' : ''}`}
                    onClick={() => setShowFootnotes(!showFootnotes)}
                    title="Afficher/Masquer les notes"
                    type="button"
                  >
                    <span className="icon">⚓</span> Notes
                  </button>
                </div>
              </div>
            </div>

            <audio ref={audioRef} src={currentAudioSrc} preload="metadata" />

            {selectedChapter.items?.length ? (
              <>
                {(() => {
                  const item = selectedChapter.items[currentDhikrIndex];
                  const repeatTarget = Number(item?.repeat ?? 0);
                  const showRepeat = Number.isFinite(repeatTarget) && repeatTarget > 0;
                  const displayedRepeats = showRepeat ? currentRepeatCount : 0;

                  return (
                    <>
                      <div className="dhikr-swipe-container">
                        <AnimatePresence initial={false} custom={direction}>
                          <DhikrCard
                            key={currentDhikrIndex}
                            item={item}
                            index={currentDhikrIndex}
                            direction={direction}
                            variants={variants}
                            canGoNext={canGoNext}
                            canGoPrev={canGoPrev}
                            goToNextDhikr={goToNextDhikr}
                            goToPrevDhikr={goToPrevDhikr}
                            handleDhikrCardClick={handleDhikrCardClick}
                            showRepeat={showRepeat}
                            repeatTarget={repeatTarget}
                            displayedRepeats={displayedRepeats}
                            currentAudioSrc={currentAudioSrc}
                            isPlaying={isPlaying}
                            handlePlayDhikr={handlePlayDhikr}
                            showTransliteration={showTransliteration}
                            showTranslation={showTranslation}
                            showFootnotes={showFootnotes}
                          />
                        </AnimatePresence>
                      </div>

                      <div className="dhikr-nav">
                        <button
                          className="dhikr-nav-btn"
                          type="button"
                          onClick={goToPrevDhikr}
                          disabled={currentDhikrIndex <= 0}
                          aria-label="Dhikr précédent"
                          title="Dhikr précédent"
                        >
                          ←
                        </button>
                        <button
                          className="dhikr-nav-btn"
                          type="button"
                          onClick={goToNextDhikr}
                          disabled={currentDhikrIndex >= selectedChapter.items.length - 1}
                          aria-label="Dhikr suivant"
                          title="Dhikr suivant"
                        >
                          →
                        </button>
                      </div>
                    </>
                  );
                })()}
              </>
            ) : (
              <p className="no-results">Aucun dhikr dans ce chapitre.</p>
            )}
          </div>
      ) : (
        <div className="chapters-list-view">
          <div className="section-head">
            <h3>La Citadelle du Musulman</h3>
          </div>

            {recentDhikr.length > 0 && (
              <div className="recent-dhikr-section">
                <div className="recent-dhikr-title">Derniers chapitres consultés</div>
                <div className="recent-dhikr-cards">
                  {recentDhikr.map((entry, idx) => {
                    const chapter = chapters.find((c) => c.id === entry?.chapterId);
                    const chapterTitleAr = entry?.chapterTitleAr || chapter?.title?.ar || "";
                    const chapterTitleFr = entry?.chapterTitleFr || chapter?.title?.fr || "";

                    return (
                      <div
                        key={`${entry.chapterId}-${entry.dhikrIndex}-${idx}`}
                        className="recent-dhikr-card"
                        onClick={() => openDhikrFromRecent(entry)}
                        title="Reprendre ce dhikr"
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            openDhikrFromRecent(entry);
                          }
                        }}
                      >
                        <button
                          type="button"
                          className="recent-dhikr-remove"
                          aria-label="Supprimer ce chapitre récent"
                          title="Supprimer"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            removeRecentChapter(entry?.chapterId);
                          }}
                        >
                          ×
                        </button>
                        {chapterTitleAr && <div className="recent-dhikr-card-ar">{chapterTitleAr}</div>}
                        {chapterTitleFr && <div className="recent-dhikr-card-fr">{chapterTitleFr}</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          
          <div className="search-container">
            <input
              type="text"
              placeholder="Rechercher un chapitre..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => {
                if (searchBlurTimeoutRef.current) {
                  clearTimeout(searchBlurTimeoutRef.current);
                  searchBlurTimeoutRef.current = null;
                }
                setIsSearchFocused(true);
              }}
              onBlur={() => {
                // Delay hiding so suggestion clicks can register
                searchBlurTimeoutRef.current = setTimeout(() => {
                  setIsSearchFocused(false);
                }, 120);
              }}
              className="search-input"
            />

              {isSearchFocused && searchTerm.trim().length > 0 && (
                <div className="search-suggestions" role="listbox" aria-label="Suggestions">
                  {chapters
                    .filter((c) => (c.title?.fr || "").toLowerCase().includes(searchTerm.toLowerCase()))
                    .slice(0, 5)
                    .map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        className="search-suggestion-btn"
                        onMouseDown={(e) => {
                          // prevent blur before click
                          e.preventDefault();
                        }}
                        onClick={() => {
                          setSearchTerm(c.title?.fr || "");
                        }}
                      >
                        {c.title?.fr}
                      </button>
                    ))}
                </div>
              )}
          </div>

          {!searchTerm && selectedCategory && (
            <div className="category-header">
              <button 
                type="button"
                className="category-back-btn" 
                onClick={() => setSelectedCategory(null)}
                aria-label="Retour aux catégories"
              >
                ←
              </button>
              <h4 className="category-header-title">{selectedCategory.fr}</h4>
            </div>
          )}

          {searchTerm ? (
            <div className="chapters-grid">
              {filteredChapters.map((chapter) => (
                <div 
                  key={chapter.id} 
                  className="chapter-card"
                  onClick={() => handleChapterClick(chapter)}
                >
                  <div className="chapter-number">{chapter.id}</div>
                  <div className="chapter-info">
                    <p className="chapter-card-title-ar">{chapter.title.ar}</p>
                    <p className="chapter-card-title-fr">{chapter.title.fr}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : selectedCategory ? (
            <div className="chapters-grid">
              {chapters
                .filter(c => c.category?.id === selectedCategory.id)
                .map((chapter) => (
                  <div 
                    key={chapter.id} 
                    className="chapter-card"
                    onClick={() => handleChapterClick(chapter)}
                  >
                    <div className="chapter-number">{chapter.id}</div>
                    <div className="chapter-info">
                      <p className="chapter-card-title-ar">{chapter.title.ar}</p>
                      <p className="chapter-card-title-fr">{chapter.title.fr}</p>
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            <div className="categories-grid">
              {categories.map((cat) => (
                <div 
                  key={cat.id} 
                  className="category-card"
                  onClick={() => setSelectedCategory(cat)}
                  role="button"
                  tabIndex={0}
                  aria-label={cat.fr || cat.en || cat.ar || "Catégorie"}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedCategory(cat);
                    }
                  }}
                >
                  <div className={`category-icon ${normalizeCategoryId(cat.id)}`} aria-hidden="true">{getCategoryVisual(cat)}</div>
                  <p className="category-title">{cat.fr}</p>
                </div>
              ))}
            </div>
          )}
          
          {searchTerm && filteredChapters.length === 0 && (
            <p className="no-results">Aucun chapitre trouvé pour "{searchTerm}"</p>
          )}
        </div>
      )}
    </div>
  </div>
  );
};

export default AdhkarPage;
